// IndexedDBを使用して録音ファイルを永続化するためのサービス
import type { RecordingFile } from "../types/models";

const DB_NAME = "interview-record-db";
const DB_VERSION = 1;
const RECORDINGS_STORE = "recordings";

/**
 * IndexedDB に保存される行の形（既存データとワイヤ互換。スキーマ移行はしない）
 */
export interface PersistedMp3File {
	fileName: string;
	fileSize: number;
	fileType: string;
	fileContent: ArrayBuffer;
}

export interface PersistedRecording {
	id: string;
	timestamp: string;
	duration: number;
	fileName: string;
	fileSize: number;
	fileType: string;
	fileContent: ArrayBuffer;
	transcriptionText: string;
	mp3File: PersistedMp3File | null;
	isConverting: boolean;
	sessionId: string;
	createdAt: number;
}

/** IDBRequest を Promise 化する */
const requestToPromise = <T>(request: IDBRequest<T>): Promise<T> =>
	new Promise((resolve, reject) => {
		request.onsuccess = () => resolve(request.result);
		request.onerror = () => reject(request.error ?? new Error("IndexedDB request failed"));
	});

/** 保存行 → アプリ内モデルへ復元 */
const toRecordingFile = (row: PersistedRecording): RecordingFile => {
	const file = new File([row.fileContent], row.fileName, { type: row.fileType });
	const mp3File = row.mp3File
		? new File([row.mp3File.fileContent], row.mp3File.fileName, { type: row.mp3File.fileType })
		: undefined;

	return {
		id: row.id,
		timestamp: row.timestamp,
		duration: row.duration,
		file,
		mp3File,
		transcriptionText: row.transcriptionText || "",
		isConverting: row.isConverting || false,
		sessionId: row.sessionId || "",
		createdAt: row.createdAt || Date.now(),
	};
};

class StorageService {
	// 同時に initDB が呼ばれても open は1回だけになるよう Promise をキャッシュする
	private dbPromise: Promise<IDBDatabase> | null = null;

	private initDB(): Promise<IDBDatabase> {
		if (this.dbPromise) {
			return this.dbPromise;
		}

		this.dbPromise = new Promise((resolve, reject) => {
			const request = indexedDB.open(DB_NAME, DB_VERSION);

			request.onerror = (event) => {
				console.error("IndexedDB接続エラー:", event);
				this.dbPromise = null; // 失敗したら次回また open を試みる
				reject(new Error("データベース接続に失敗しました"));
			};

			request.onsuccess = () => {
				resolve(request.result);
			};

			request.onupgradeneeded = () => {
				const db = request.result;

				// 既存のストアが存在する場合は削除（データをリセット）
				if (db.objectStoreNames.contains(RECORDINGS_STORE)) {
					db.deleteObjectStore(RECORDINGS_STORE);
				}

				// 新しいストアを作成
				const store = db.createObjectStore(RECORDINGS_STORE, { keyPath: "id" });

				// よく使用するフィールドにインデックスを作成
				store.createIndex("timestamp", "timestamp", { unique: false });
				store.createIndex("sessionId", "sessionId", { unique: false });
				store.createIndex("createdAt", "createdAt", { unique: false });
			};
		});

		return this.dbPromise;
	}

	private async getStore(mode: IDBTransactionMode): Promise<IDBObjectStore> {
		const db = await this.initDB();
		return db.transaction([RECORDINGS_STORE], mode).objectStore(RECORDINGS_STORE);
	}

	/**
	 * 録音ファイルと関連情報をIndexedDBに保存（同一idは上書き）
	 */
	async saveRecording(recording: RecordingFile): Promise<string> {
		// File → ArrayBuffer に変換
		const fileContent = await recording.file.arrayBuffer();

		let mp3FileData: PersistedMp3File | null = null;
		if (recording.mp3File) {
			try {
				mp3FileData = {
					fileName: recording.mp3File.name,
					fileSize: recording.mp3File.size,
					fileType: recording.mp3File.type,
					fileContent: await recording.mp3File.arrayBuffer(),
				};
			} catch (err) {
				console.warn("MP3ファイルの変換に失敗しました:", err);
			}
		}

		const row: PersistedRecording = {
			id: recording.id,
			timestamp: recording.timestamp,
			duration: recording.duration,
			fileName: recording.file.name,
			fileSize: recording.file.size,
			fileType: recording.file.type,
			fileContent,
			transcriptionText: recording.transcriptionText || "",
			mp3File: mp3FileData,
			isConverting: recording.isConverting || false,
			sessionId: recording.sessionId || "",
			createdAt: recording.createdAt || Date.now(),
		};

		const store = await this.getStore("readwrite");
		await requestToPromise(store.put(row));
		return recording.id;
	}

	/**
	 * 録音データをIDで取得
	 */
	async getRecording(id: string): Promise<RecordingFile | null> {
		const store = await this.getStore("readonly");
		const row = (await requestToPromise(store.get(id))) as PersistedRecording | undefined;
		return row ? toRecordingFile(row) : null;
	}

	/**
	 * すべての録音データを取得。
	 * タブがMP3変換中に閉じられて isConverting: true のまま残った行は false に修復する。
	 */
	async getAllRecordings(): Promise<RecordingFile[]> {
		const store = await this.getStore("readonly");
		const rows = (await requestToPromise(store.getAll())) as PersistedRecording[];

		// isConverting の残骸を修復（変換処理はページを跨いで継続しないため）
		const staleRows = rows.filter((row) => row.isConverting);
		if (staleRows.length > 0) {
			const writeStore = await this.getStore("readwrite");
			await Promise.all(
				staleRows.map((row) => {
					row.isConverting = false;
					return requestToPromise(writeStore.put(row));
				}),
			);
		}

		return rows.map(toRecordingFile);
	}

	/**
	 * 録音データを削除
	 */
	async deleteRecording(id: string): Promise<boolean> {
		const store = await this.getStore("readwrite");
		await requestToPromise(store.delete(id));
		return true;
	}

	/**
	 * すべての録音データを削除
	 */
	async clearAllRecordings(): Promise<boolean> {
		const store = await this.getStore("readwrite");
		await requestToPromise(store.clear());
		return true;
	}
}

// シングルトンインスタンスをエクスポート
export const storageService = new StorageService();
